/**
 * Our Place Testimonial Carousel
 * File Location: assets/testimonial-carousel.js
 *
 * 功能：
 * 1. Splide 轮播初始化（修复 .shopify-block 层问题）
 * 2. 视频分级加载状态机（IDLE → METADATA → READY → PLAYING）
 * 3. 视频方向检测（竖屏保持 / 横屏切换模糊背景模式）
 * 4. 双层视频同步播放
 * 5. 网络质量感知
 * 6. Shopify 编辑器兼容
 */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════
     状态常量
     ══════════════════════════════════════════════ */
  var VIDEO_STATE = {
    IDLE     : 'idle',      // src 未设置，完全不占带宽
    METADATA : 'metadata',  // 只加载头部信息（几 KB）
    READY    : 'ready',     // 完整缓冲，随时可播放
    PLAYING  : 'playing'    // 正在播放
  };

  /* ══════════════════════════════════════════════
     网络质量检测
     ══════════════════════════════════════════════ */
  function getNetworkQuality() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return 'unknown';
    return conn.effectiveType || 'unknown'; // '4g' | '3g' | '2g' | 'slow-2g'
  }

  function isSlowNetwork() {
    var quality = getNetworkQuality();
    return quality === '2g' || quality === 'slow-2g';
  }

  /* ══════════════════════════════════════════════
     视频状态管理
     ══════════════════════════════════════════════ */

  /**
   * 设置视频容器的加载状态
   * @param {Element} container - .ugc-video-column 元素
   * @param {string} state - VIDEO_STATE 中的值
   */
  function setVideoState(container, state) {
    if (!container) return;
    container.dataset.videoState = state;
  }

  /**
   * 将视频提升到 METADATA 状态
   * 只下载视频头部（用于获取尺寸，触发方向检测）
   * @param {Element} container - .ugc-video-column 元素
   */
  function loadVideoMetadata(container) {
    if (!container) return;
    var currentState = container.dataset.videoState;
    if (currentState !== VIDEO_STATE.IDLE) return; // 已经在加载或更高状态

    var fgVideo = container.querySelector('.video-fg-element');
    var bgVideo = container.querySelector('.video-bg-element');
    if (!fgVideo) return;

    var src = fgVideo.dataset.src;
    if (!src) return;

    // 设置 src 但只加载元数据
    fgVideo.preload = 'metadata';
    fgVideo.src = src;

    if (bgVideo && bgVideo.dataset.src) {
      bgVideo.preload = 'metadata';
      bgVideo.src = bgVideo.dataset.src;
    }

    setVideoState(container, VIDEO_STATE.METADATA);

    // 监听元数据加载完成 → 触发方向检测
    fgVideo.addEventListener('loadedmetadata', function onMeta() {
      fgVideo.removeEventListener('loadedmetadata', onMeta);
      detectVideoOrientation(container, fgVideo);
    });
  }

  /**
   * 将视频提升到 READY 状态
   * 开始完整缓冲视频内容
   * @param {Element} container - .ugc-video-column 元素
   */
  function loadVideoFull(container) {
    if (!container) return;
    var currentState = container.dataset.videoState;

    // IDLE 状态先跳过 METADATA 直接加载（当前卡片）
    if (currentState === VIDEO_STATE.IDLE) {
      var fgVideo = container.querySelector('.video-fg-element');
      var bgVideo = container.querySelector('.video-bg-element');

      if (fgVideo && fgVideo.dataset.src) {
        fgVideo.src = fgVideo.dataset.src;
        fgVideo.addEventListener('loadedmetadata', function onMeta() {
          fgVideo.removeEventListener('loadedmetadata', onMeta);
          detectVideoOrientation(container, fgVideo);
        });
      }

      if (bgVideo && bgVideo.dataset.src) {
        bgVideo.src = bgVideo.dataset.src;
      }
    }

    // 切换到完整预加载
    var fgVid = container.querySelector('.video-fg-element');
    var bgVid = container.querySelector('.video-bg-element');

    if (fgVid && fgVid.src) {
      fgVid.preload = 'auto';
    }
    if (bgVid && bgVid.src) {
      bgVid.preload = 'auto';
    }

    setVideoState(container, VIDEO_STATE.READY);
  }

  /**
   * 播放视频（当前激活卡片）
   * @param {Element} container - .ugc-video-column 元素
   */
  function playVideo(container) {
    if (!container) return;

    // 确保已经加载
    loadVideoFull(container);

    var fgVideo = container.querySelector('.video-fg-element');
    var bgVideo = container.querySelector('.video-bg-element');

    if (!fgVideo) return;

    // 播放前景视频
    var playPromise = fgVideo.play();
    if (playPromise !== undefined) {
      playPromise.catch(function (err) {
        // 自动播放被阻止（通常是 iOS 未静音）
        console.warn('Video autoplay blocked:', err);
      });
    }

    // 同步播放背景视频
    if (bgVideo && bgVideo.src) {
      bgVideo.currentTime = fgVideo.currentTime;
      bgVideo.play().catch(function () {});
    }

    setVideoState(container, VIDEO_STATE.PLAYING);

    // 同步背景视频进度
    syncVideoLayers(container);
  }

  /**
   * 暂停视频（离开当前卡片时）
   * @param {Element} container - .ugc-video-column 元素
   */
  function pauseVideo(container) {
    if (!container) return;
    var fgVideo = container.querySelector('.video-fg-element');
    var bgVideo = container.querySelector('.video-bg-element');

    if (fgVideo && !fgVideo.paused) fgVideo.pause();
    if (bgVideo && !bgVideo.paused) bgVideo.pause();

    var currentState = container.dataset.videoState;
    if (currentState === VIDEO_STATE.PLAYING) {
      setVideoState(container, VIDEO_STATE.READY);
    }
  }

  /**
   * 释放视频（距离当前卡片超过2张时）
   * @param {Element} container - .ugc-video-column 元素
   */
  function releaseVideo(container) {
    if (!container) return;
    var currentState = container.dataset.videoState;
    if (currentState === VIDEO_STATE.IDLE) return; // 已经是 IDLE

    var fgVideo = container.querySelector('.video-fg-element');
    var bgVideo = container.querySelector('.video-bg-element');

    if (fgVideo) {
      fgVideo.pause();
      fgVideo.removeAttribute('src');
      fgVideo.load(); // 释放内存
    }
    if (bgVideo) {
      bgVideo.pause();
      bgVideo.removeAttribute('src');
      bgVideo.load();
    }

    setVideoState(container, VIDEO_STATE.IDLE);
  }

  /**
   * 同步前景视频和背景视频的播放进度
   * @param {Element} container - .ugc-video-column 元素
   */
  function syncVideoLayers(container) {
    var fgVideo = container.querySelector('.video-fg-element');
    var bgVideo = container.querySelector('.video-bg-element');

    if (!fgVideo || !bgVideo || !bgVideo.src) return;

    // 监听前景视频事件，同步到背景视频
    fgVideo.addEventListener('play', function () {
      bgVideo.currentTime = fgVideo.currentTime;
      bgVideo.play().catch(function () {});
    });

    fgVideo.addEventListener('pause', function () {
      bgVideo.pause();
    });

    fgVideo.addEventListener('seeked', function () {
      bgVideo.currentTime = fgVideo.currentTime;
    });
  }

  /* ══════════════════════════════════════════════
     视频方向检测（方案 C 核心）
     ══════════════════════════════════════════════ */

  /**
   * 检测视频是横屏还是竖屏，并切换对应样式
   * @param {Element} container - .ugc-video-column 元素
   * @param {HTMLVideoElement} video - 前景视频元素
   */
  function detectVideoOrientation(container, video) {
    var vw = video.videoWidth;
    var vh = video.videoHeight;

    if (!vw || !vh) return; // 尺寸未获取到

    var ratio = vw / vh;
    var isLandscape = ratio > 1; // 宽 > 高 = 横屏

    if (isLandscape) {
      // 横屏：显示模糊背景 + 前景完整显示
      container.classList.add('is-landscape');
    } else {
      // 竖屏：隐藏模糊背景，视频填满容器
      container.classList.remove('is-landscape');
    }
  }

  /* ══════════════════════════════════════════════
     分级加载调度器
     根据当前卡片索引，更新所有卡片的加载状态
     ══════════════════════════════════════════════ */

  /**
   * @param {Element} sliderEl - .testimonial-splide 元素
   * @param {number} currentIndex - 当前激活的卡片索引
   */
  function scheduleVideoLoading(sliderEl, currentIndex) {
    var containers = Array.from(
      sliderEl.querySelectorAll('.ugc-video-column')
    );

    var total = containers.length;
    if (total === 0) return;

    var slowNetwork = isSlowNetwork();

    containers.forEach(function (container, i) {
      // 计算当前卡片与目标卡片的距离（loop 模式下取最短路径）
      var dist = Math.min(
        Math.abs(i - currentIndex),
        total - Math.abs(i - currentIndex)
      );

      if (dist === 0) {
        // 当前卡片：完整加载 + 播放
        playVideo(container);

      } else if (dist === 1 && !slowNetwork) {
        // 相邻卡片：预加载元数据（获取尺寸，准备方向检测）
        // 慢网络下跳过预加载
        loadVideoMetadata(container);

      } else if (dist <= 2 && !slowNetwork) {
        // 次相邻：保持 METADATA 状态（不再进一步加载）
        // 什么都不做（保持现有状态）

      } else {
        // 超出范围：释放视频资源
        releaseVideo(container);
      }
    });
  }

  /* ══════════════════════════════════════════════
     Splide 初始化
     ══════════════════════════════════════════════ */
  function initOfficialCarousel() {
    var sliders = document.querySelectorAll('.testimonial-splide');

    sliders.forEach(function (sliderEl) {
      // 销毁旧实例
      if (sliderEl.splideInstance) {
        try { sliderEl.splideInstance.destroy(true); } catch (e) {}
        sliderEl.splideInstance = null;
      }

      if (typeof Splide === 'undefined') {
        console.error('Splide not loaded');
        return;
      }

      /* ── 关键修复：给 .shopify-block 添加 .splide__slide 类 ── */
      var shopifyBlocks = sliderEl.querySelectorAll('.splide__list > .shopify-block');

      if (shopifyBlocks.length === 0) {
        console.warn('No .shopify-block found. Check content_for usage.');
        return;
      }

      shopifyBlocks.forEach(function (block) {
        block.classList.add('splide__slide');
      });

      /* ── Splide 实例化 ── */
      var splide = new Splide(sliderEl, {
        type       : 'loop',
        autoWidth  : true,
        focus      : 0,
        gap        : 50,
        pagination : true,
        arrows     : false,
        drag       : true,
        flickPower : 300,
        breakpoints: {
          1023: { gap: 20 },
          767 : { gap: 16 }
        }
      });

      /* ── 轮播切换时更新视频加载状态 ── */
      splide.on('mounted', function () {
        // 页面加载完成后初始化加载调度
        scheduleVideoLoading(sliderEl, splide.index);
      });

      splide.on('move', function (newIndex) {
        // 先暂停之前正在播放的视频
        var allContainers = sliderEl.querySelectorAll('.ugc-video-column');
        allContainers.forEach(function (c) {
          if (c.dataset.videoState === VIDEO_STATE.PLAYING) {
            pauseVideo(c);
          }
        });

        // 更新加载调度
        scheduleVideoLoading(sliderEl, newIndex);
      });

      splide.mount();
      sliderEl.splideInstance = splide;

      /* ── 绑定自定义箭头按钮 ── */
      var sectionEl = sliderEl.closest('.shopify-section') || document;

      // 克隆节点移除旧事件，防止重复绑定
      sectionEl.querySelectorAll('.btn-prev-slide').forEach(function (btn) {
        var newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        newBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (sliderEl.splideInstance) sliderEl.splideInstance.go('<');
        });
      });

      sectionEl.querySelectorAll('.btn-next-slide').forEach(function (btn) {
        var newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        newBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (sliderEl.splideInstance) sliderEl.splideInstance.go('>');
        });
      });

    }); // end sliders.forEach
  }

  /* ══════════════════════════════════════════════
     页面加载入口
     ══════════════════════════════════════════════ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOfficialCarousel);
  } else {
    initOfficialCarousel();
  }

  /* ══════════════════════════════════════════════
     Shopify 编辑器兼容
     ══════════════════════════════════════════════ */
  if (window.Shopify && Shopify.designMode) {

    document.addEventListener('shopify:section:load', function () {
      setTimeout(initOfficialCarousel, 150);
    });

    document.addEventListener('shopify:block:select', function (event) {
      var targetBlockId = event.detail.blockId;
      var sectionId     = event.detail.sectionId;

      setTimeout(function () {
        initOfficialCarousel();

        var targetSection = document.querySelector('[data-section-id="' + sectionId + '"]');
        if (!targetSection) return;

        var sliderEl = targetSection.querySelector('.testimonial-splide');
        if (!sliderEl || !sliderEl.splideInstance) return;

        var allSlides = Array.from(
          sliderEl.querySelectorAll('.splide__list > .shopify-block[data-block-id]')
        );

        var targetIndex = allSlides.findIndex(function (slide) {
          return slide.dataset.blockId === targetBlockId;
        });

        if (targetIndex !== -1) {
          setTimeout(function () {
            sliderEl.splideInstance.go(targetIndex);
          }, 100);
        }
      }, 100);
    });
  }

  /* ══════════════════════════════════════════════
     全局暴露（供 section inline script 调用）
     ══════════════════════════════════════════════ */
  window.initOfficialCarousel = initOfficialCarousel;

})();